import { createReactExtension } from '@backstage/core-plugin-api';
import { datadogRum } from '@datadog/browser-rum';
import { FavoriteLinksSettingsDialog } from './favoriteLinksSettingsDialog/FavoriteLinksSettingsDialog';
import { homePlugin } from '@backstage/plugin-home';
import { ILinkItem } from './types/LinkItem.interface';
import { InfoCard } from '@backstage/core-components';
import { NoContentSelected } from '../noContentSelected/NoContentSelected';
import { NoContentType } from '../noContentSelected/types/NoContentType.enum';
import { PaletteColor } from '@mui/material/styles';
import { useEffect } from 'react';
import { useStyles } from './favoriteLinks.style';
import { useTheme } from '@material-ui/core';

import { CardWithGradientBackground, useUserTeams } from '@internal/ui-common';

const schemaForSettings = {
  title: 'Your favorite links',
  type: 'object',
  properties: {
    links: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          name: { type: 'string' },
          href: { type: 'string' },
          color: { type: 'string' },
        },
        required: ['name', 'href', 'color'],
      },
      default: [] as ILinkItem[],
    },
  },
};

function FavoriteLinks({ links }: { links?: ILinkItem[] }) {
  const { cx, classes } = useStyles();
  const theme = useTheme();
  const { teams } = useUserTeams();

  useEffect(() => {
    datadogRum.addAction('favorite_links_widget_present', {
      present: true,
      linksCount: links?.length ?? 0,
    });
  }, [links?.length]);

  const teamLinks: ILinkItem[] = teams.map(team => ({
    name: team.name,
    href: `${window.location.origin}${team.portalTeamPageHref}`,
    color: 'primary',
  }));

  schemaForSettings.properties.links.default = [
    ...schemaForSettings.properties.links.default.filter(
      link => !teamLinks.some(teamLink => teamLink.href === link.href),
    ),
    ...teamLinks,
  ];

  return (
    <section className={cx(classes.linksContainer)}>
      {links?.map(link => {
        const isExternal = !link.href.includes(window.location.origin);
        const href = isExternal
          ? link.href
          : link.href.replace(window.location.origin, '');

        return (
          <div key={link.href} className={cx(classes.cardContainer)}>
            <CardWithGradientBackground
              title={link.name}
              color={(theme.palette[link.color] as PaletteColor)?.main}
              href={href}
              isExternal={isExternal}
            />
          </div>
        );
      })}

      {(links === undefined || links.length === 0) && (
        <InfoCard title="Favorite links">
          <NoContentSelected type={NoContentType.Link} />
        </InfoCard>
      )}
    </section>
  );
}

export { FavoriteLinks };

export const FavoriteLinksWidget = homePlugin.provide(
  createReactExtension({
    name: 'Your favorite links widget',
    component: {
      lazy: () => import('./FavoriteLinks').then(m => m.FavoriteLinks),
    },
    data: {
      title: 'Your favorite links',
      description:
        'Shows a list of customizable, internal or external, personal links.',
      'home.widget.config': {
        layout: {
          width: { defaultColumns: 4, minColumns: 2 },
          height: { defaultRows: 4, minRows: 2 },
        },
        settings: {
          schema: schemaForSettings,
          uiSchema: {
            links: {
              'ui:widget': FavoriteLinksSettingsDialog,
            },
          },
        },
      },
    },
  }),
);
